import { structuredClonePolyfill } from "../utils/structuredClonePolyfill";

export const createIframe = (src: string): HTMLIFrameElement => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("allow", "autoplay *; gyroscope *; wake-lock *; accelerometer *; encrypted-media *");
    iframe.setAttribute("seamless", "seamless");
    iframe.setAttribute("name", "game");
    iframe.setAttribute("class", "game-frame");

    const url = new URL(src);
    url.searchParams.set("reloadTS", Date.now().toString());

    iframe.setAttribute("src", url.toString());

    return iframe;
};

export const addLoader = (): HTMLDivElement => {
    const container = document.createElement("div");
    container.setAttribute("class", "loading-container");

    const block = document.createElement("div");
    block.setAttribute("class", "loading-block");
    container.appendChild(block);

    return container;
};

export const showError = (message: string): void => {
    const overlay = document.createElement("div");
    overlay.setAttribute("class", "error-modal-overlay");
    const modal = document.createElement("div");
    modal.setAttribute("class", "error-modal");

    const errorMessage = document.createElement("div");
    errorMessage.setAttribute("class", "error-message");
    errorMessage.textContent = message;

    modal.appendChild(errorMessage);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);
};

export const loadText = async (url: string): Promise<string> => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to load "${url}": ${response.statusText}`);
    }

    return await response.text();
};

export const loadXML = async (url: string): Promise<Document> => {
    const response = await fetch(url);

    if (!response.ok) {
        throw new Error(`Failed to load XML "${url}": ${response.statusText}`);
    }

    const text = await response.text();
    const xmlDoc = new window.DOMParser().parseFromString(
        text,
        "application/xml"
    );

    const parserError = xmlDoc.querySelector("parsererror");
    if (parserError) {
        console.error(
            `[XML parse error] at "${url}": `,
            parserError.textContent
        );
        throw new Error(`Invalid XML at "${url}"`);
    }

    return xmlDoc;
};

export const mergeObject = <T>(
    target: Partial<T>,
    source: Partial<T>
): Partial<T> => {
    const result = structuredClonePolyfill(target);
    const keys: string[] = Object.keys(source);

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];

        if ((source as any)[key] === undefined || (source as any)[key] === "") {
            continue;
        }

        (result as any)[key] = (source as any)[key];
    }

    return result;
};

export const compileWhereToFn = (expr: string, debug = false): Function => {
    const ops = {
        "=": "===",
        "==": "===",
        "!=": "!==",
        "<>": "!==",
        ">=": ">=",
        "<=": "<=",
        ">": ">",
        "<": "<",
    };

    const sanitize = (v: string): string => {
        v = v.trim();
        if (
            /^["'].*["']$/.test(v) ||
            /^-?\d+(\.\d+)?$/.test(v) ||
            v === "true" ||
            v === "false"
        )
            return v;
        return `obj.${v}`;
    };

    expr = expr
        .replace(/\bAND\b/gi, "&&")
        .replace(/\bOR\b/gi, "||")
        .replace(/(\w+)\s+NOT\s+IN\s*\(([^)]+)\)/gi, (_, key, list) => {
            const values = `[${list.split(",").map(sanitize)}]`;
            return `!${values}.includes(${sanitize(key)})`;
        })
        .replace(/(\w+)\s+IN\s*\(([^)]+)\)/gi, (_, key, list) => {
            const values = `[${list.split(",").map(sanitize)}]`;
            return `${values}.includes(${sanitize(key)})`;
        });

    for (const [sqlOp, jsOp] of Object.entries(ops)) {
        const pattern = new RegExp(
            `(\\w+)\\s*\\${sqlOp}\\s*(\\w+|["'][^"']*["']|\\d+(\\.\\d+)?)`,
            "g"
        );
        expr = expr.replace(
            pattern,
            (_, a, b) => `${sanitize(a)} ${jsOp} ${sanitize(b)}`
        );
    }

    if (debug) {
        console.warn(`Exp:`, `"return ${expr};"`);
    }

    return new Function("obj", `return ${expr}`);
};

export const validateUrl = (url: string, message?: string, withThrow = true): URL | undefined => {
    try {
        return new URL(url);
    } catch (e) {
        if (withThrow) {
            throw new Error(message || `Invalid URL: ${url}`);
        }

        console.warn(message || `Invalid URL: ${url};`);
        return undefined;
    }
}

export const isValidAbsoluteUrl = (url: string): boolean => {
    try {
        const u = new URL(url);
        return !!u.protocol && !!u.hostname;
    } catch {
        return false;
    }
}
